import { useEffect, useState } from 'react';

import { adminApi } from '../adminApi';
import { formatDate } from '../format';
import {
  panelChangeFailuresPath,
  panelChangeFailuresSummary,
  rateLabel,
  type PanelChangeFailureSummary,
} from '../panelChangeFailures';

// Failed panel changes (TOM-1028): a refused panel change is a bug, counted
// with its reason. Each day's count and rate per 100 Neo panel-change turns,
// the top codes, and the newest rows. Codes and counts only, so no reason is
// asked.

const WINDOWS = [7, 14, 30, 90] as const;

/** The days with a failure or a panel-change turn, newest first: an empty day says nothing. */
function activeDays(s: PanelChangeFailureSummary) {
  return [...s.daily].reverse().filter((d) => d.failures > 0 || d.panelChangeTurns > 0);
}

export function PanelChangeFailuresPanel() {
  const [days, setDays] = useState<number>(14);
  const [summary, setSummary] = useState<PanelChangeFailureSummary | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let ignore = false;
    setSummary(null);
    setFailed(false);
    adminApi<PanelChangeFailureSummary>(panelChangeFailuresPath(days))
      .then((res) => {
        if (!ignore) setSummary(res);
      })
      .catch(() => {
        if (!ignore) setFailed(true);
      });
    return () => {
      ignore = true;
    };
  }, [days]);

  return (
    <div className="admin-info-card" data-testid="panel-change-failures">
      <h2>Failed panel changes</h2>
      <form
        className="eval-filters"
        aria-label="Failed panel changes window"
        onSubmit={(e) => e.preventDefault()}
      >
        <label>
          Window
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {WINDOWS.map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        </label>
      </form>
      {failed ? (
        <p className="admin-muted">The count could not be read. Try again in a moment.</p>
      ) : summary === null ? (
        <p className="admin-loading-row">Loading…</p>
      ) : (
        <>
          <p>{panelChangeFailuresSummary(summary)}</p>
          {summary.topCodes.length > 0 ? (
            <>
              <h3>Top codes</h3>
              <div className="scroll-x">
                <table className="admin-table" data-testid="panel-change-failure-codes">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.topCodes.map((c) => (
                      <tr key={c.code}>
                        <td>
                          <code>{c.code}</code>
                        </td>
                        <td>{c.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
          <h3>By day</h3>
          {activeDays(summary).length === 0 ? (
            <p className="admin-muted">No Neo panel changes or failures on any day.</p>
          ) : (
            <div className="scroll-x">
              <table className="admin-table" data-testid="panel-change-failure-days">
                <thead>
                  <tr>
                    <th>Day (UTC)</th>
                    <th>Failures</th>
                    <th>Neo</th>
                    <th>Owner</th>
                    <th>Failed turns</th>
                    <th>Panel-change turns</th>
                    <th>Per 100 turns</th>
                  </tr>
                </thead>
                <tbody>
                  {activeDays(summary).map((d) => (
                    <tr key={d.day}>
                      <td>{d.day}</td>
                      <td>{d.failures}</td>
                      <td>{d.neoFailures}</td>
                      <td>{d.ownerFailures}</td>
                      <td>{d.failedTurns}</td>
                      <td>{d.panelChangeTurns}</td>
                      <td>{rateLabel(d.ratePer100)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {summary.recent.length > 0 ? (
            <>
              <h3>Newest</h3>
              <div className="scroll-x">
                <table className="admin-table" data-testid="panel-change-failure-recent">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Code</th>
                      <th>Boat</th>
                      <th>Board</th>
                      <th>By</th>
                      <th>Where</th>
                      <th>Receipt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.recent.map((r) => (
                      <tr key={r.id}>
                        <td>{formatDate(r.createdAt)}</td>
                        <td>
                          <code>{r.code}</code>
                        </td>
                        <td>{r.boatName ?? '—'}</td>
                        <td>{r.boardSlug !== null ? <code>{r.boardSlug}</code> : '—'}</td>
                        <td>
                          {r.writer === 'owner' ? 'Owner' : r.writer === 'neo' ? 'Neo' : 'System'}
                        </td>
                        <td>
                          <code>{r.source}</code>
                          {r.errorName !== null ? (
                            <span className="admin-muted"> · {r.errorName}</span>
                          ) : null}
                        </td>
                        <td>{r.receiptStatus ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
